import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { fetchPriceHistory } from '@/api/games'
import { Icon } from './Icon'
import { PriceChart } from './PriceChart'

interface ItadTooltipProps {
  gameId: string
  currency: string
  children: React.ReactNode
}

interface Position {
  left: number
  top: number
  placement: 'top' | 'bottom'
}

const GAP = 8
const MARGIN = 8
// Horizontal padding inside .cg-atl-tooltip (10px 12px → 24px total).
const PADDING_X = 24

export function ItadTooltip({ gameId, currency, children }: ItadTooltipProps) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<Position | null>(null)
  const [chartW, setChartW] = useState(0)
  const triggerRef = useRef<HTMLSpanElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['price-history', gameId],
    queryFn: () => fetchPriceHistory(gameId),
    enabled: open,
    staleTime: 30 * 60 * 1000,
    retry: false,
  })

  // The popover's width comes from CSS (responsive breakpoints); we measure the
  // rendered element to right-align it to the trigger, flip when it would
  // overflow, and to size the SVG chart to fill it.
  const place = useCallback(() => {
    const el = triggerRef.current
    const pop = popRef.current
    if (!el || !pop) return
    const r = el.getBoundingClientRect()
    const popW = pop.offsetWidth
    const popH = pop.offsetHeight
    const left = Math.max(MARGIN, Math.min(r.right - popW, window.innerWidth - popW - MARGIN))
    const fitsBelow = r.bottom + GAP + popH + MARGIN <= window.innerHeight
    const placement = fitsBelow ? 'bottom' : 'top'
    const top = placement === 'bottom' ? r.bottom + GAP : Math.max(MARGIN, r.top - GAP - popH)
    setPos({ left, top, placement })
    setChartW(pop.clientWidth - PADDING_X)
  }, [])

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const handleOpen = useCallback(() => {
    // Don't show the hover popover on touch-only devices.
    if (typeof window !== 'undefined' && !window.matchMedia('(hover: hover)').matches) return
    cancelClose()
    setOpen(true)
  }, [cancelClose])

  // Grace period lets the cursor cross the gap into the popover without closing.
  const handleClose = useCallback(() => {
    cancelClose()
    closeTimer.current = setTimeout(() => setOpen(false), 120)
  }, [cancelClose])

  // Touch devices get no hover, so tapping the trigger toggles the popover.
  // Stop propagation so the tap doesn't also toggle the parent wishlist row.
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      cancelClose()
      setOpen((prev) => !prev)
    },
    [cancelClose],
  )

  // Measure & position after the popover mounts and whenever its content height
  // changes (loading → chart). useLayoutEffect avoids a visible reflow.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    place()
  }, [open, place, isLoading, data])

  useEffect(() => {
    if (!open) return
    const onScrollOrResize = () => place()
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [open, place])

  // Dismiss when tapping outside (touch, where there's no mouseleave) or Escape.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node
      if (triggerRef.current?.contains(target) || popRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  useEffect(() => () => cancelClose(), [cancelClose])

  return (
    <span
      ref={triggerRef}
      className="cg-atl-tooltip-wrap"
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
      onClick={handleClick}
    >
      {children}
      {open &&
        createPortal(
          <div
            ref={popRef}
            className="cg-atl-tooltip"
            role="tooltip"
            style={{
              left: pos?.left ?? 0,
              top: pos?.top ?? 0,
              visibility: pos ? 'visible' : 'hidden',
            }}
            onMouseEnter={cancelClose}
            onMouseLeave={handleClose}
          >
            <div className="cg-atl-tooltip-title">Price History</div>
            {isLoading ? (
              <div className="cg-atl-tooltip-status">Loading…</div>
            ) : (
              chartW > 0 && (
                <PriceChart points={data?.points ?? []} currency={currency} width={chartW} />
              )
            )}
            {data?.itadGameId && (
              <a
                className="cg-atl-tooltip-link"
                href={`https://isthereanydeal.com/game/id:${data.itadGameId}/`}
                target="_blank"
                rel="noopener noreferrer"
              >
                View on ITAD
                <Icon name="external" size={11} />
              </a>
            )}
          </div>,
          document.body,
        )}
    </span>
  )
}
